/**
 * rag.ts — Base de conhecimento local (RAG) na administração (F11).
 *
 * Restrita ao super admin. A importação de acórdãos do TCU continua na
 * rota legada: é o coletor da API do Tribunal, com paginação e filtro por
 * ano — pipeline pesado que a SPA reaproveita (§3 do plano).
 */

import type { components } from './generated/api'
import { api, apiLegado } from './http'

export type TipoRag = components['schemas']['TipoRag']
export type DetalheTipoRag = components['schemas']['DetalheTipoRag']
export type DocumentoRag = components['schemas']['DocumentoRag']
export type ChunksDoDocumento = components['schemas']['ChunksDoDocumento']

export function tiposDeRag() {
  return api<{ tipos: TipoRag[]; disponivel: boolean }>('/api/v1/rag/tipos')
}

export function criarTipoDeRag(nome: string, descricao: string) {
  return api<DetalheTipoRag>('/api/v1/rag/tipos', {
    method: 'POST',
    body: { nome, descricao },
  })
}

export function detalheTipoDeRag(id: number) {
  return api<DetalheTipoRag>(`/api/v1/rag/tipos/${id}`)
}

export function enviarDocumentoRag(tipoId: number, arquivo: File) {
  const form = new FormData()
  form.append('arquivo', arquivo)
  return api<{ id: number | null; nome: string; chunks: number; mensagem: string }>(
    `/api/v1/rag/tipos/${tipoId}/documentos`,
    { method: 'POST', form },
  )
}

export function excluirDocumentosRag(documentos: number[]) {
  return api<{ removidos: number; falhas: number }>('/api/v1/rag/documentos', {
    method: 'DELETE',
    body: { documentos },
  })
}

export function chunksDoDocumento(id: number) {
  return api<ChunksDoDocumento>(`/api/v1/rag/documentos/${id}/chunks`)
}

export interface AcordaoTcu {
  numero?: string
  ano?: string
  colegiado?: string
  titulo?: string
  relator?: string
  data?: string
  tipo?: string
  sumario?: string
  ja_existe?: boolean
  relevante?: boolean
  /** 'relevante' | 'irrelevante' | 'indecidivel' — ver classificar() */
  veredito?: string
  /** o veredito saiu do inteiro teor (PDF), não do metadado */
  lido_do_pdf?: boolean
  /** termos de licitação encontrados — a evidência do veredito */
  termos?: string[]
  /** trecho do acórdão em volta do primeiro termo encontrado */
  trecho?: string
  [chave: string]: unknown
}

/** Diagnóstico da varredura por ano (a API do TCU não filtra por ano). */
export interface VarreduraTcu {
  ano: string
  paginas: number
  registros_lidos: number
  anos_encontrados: string[]
  motivo: string
}

export function previaAcordaosTcu(
  quantidade: number,
  ano: string,
  tipoId: number,
) {
  const parametros = new URLSearchParams({
    quantidade: String(quantidade),
    rag_type_id: String(tipoId),
    apenas_relevantes: 'true',
  })
  if (ano) parametros.set('ano', ano)
  return apiLegado<{
    success: boolean
    acordaos: AcordaoTcu[]
    count?: number
    novos_count?: number
    relevantes_count?: number
    indecidiveis_count?: number
    lidos_do_pdf?: number
    varredura?: VarreduraTcu | null
  }>(`/rag/admin/preview_tcu?${parametros.toString()}`)
}

export function importarAcordaosTcu(
  tipoId: number,
  quantidade: number,
  ano: string,
) {
  return apiLegado<{
    success: boolean
    message?: string
    importados?: number
    ignorados?: number
  }>(`/rag/admin/import_tcu/${tipoId}`, {
    method: 'POST',
    body: { quantidade, ano: ano || undefined },
  })
}
